import React, { useState, useMemo } from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { QUICK_MARKETS } from "./constants";
import MatkaFooter from "./MatkaFooter";
import MatkaSessionSelect, {
  applySessionToBids,
  getSessionOptions,
} from "./MatkaSessionSelect";

export default function MatkaJodiDigitBulk({
  selectedMarket,
  selectedGameType,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onSelectTab,
  onPlaceBet,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [inputPoints, setInputPoints] = useState("");
  const [inputJodi, setInputJodi] = useState("JODI DIGIT");
  const [gameType, setGameType] = useState(() =>
    getSessionOptions(selectedGameType?.betDirection)[0]
  );
  const [bids, setBids] = useState([]);
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: gameType,
    bids,
    onPlaced: () => setBids([]),
  });

  const totalBids = bids.length;
  const totalPoints = useMemo(() => {
    return bids.reduce((acc, b) => acc + (Number(b.points) || 0), 0);
  }, [bids]);

  const handleAddBid = (e) => {
    e.preventDefault();
    if (!inputPoints || !inputJodi) return;

    const newBid = {
      id: Date.now(),
      digit: inputJodi.trim(),
      points: Number(inputPoints),
      gameType,
    };

    setBids([newBid, ...bids]);
    setInputPoints("");
  };

  const handleGameTypeChange = (nextGameType) => {
    setGameType(nextGameType);
    setBids((currentBids) =>
      applySessionToBids(currentBids, nextGameType, (bid) => bid.digit)
    );
  };

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Bar for Betting Screen */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((qm, idx) => (
              <React.Fragment key={qm.id}>
                <button
                  type="button"
                  className={`matka-quick-market-btn ${
                    selectedMarket?.id === qm.id ? "active" : ""
                  }`}
                  onClick={() => onQuickMarketSelect(qm.id)}
                >
                  {qm.icon && <span className="me-1">{qm.icon}</span>}
                  {qm.name}
                </button>
                {idx < QUICK_MARKETS.length - 1 && (
                  <span className="matka-switcher-dot">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* User Auth Buttons */}
          <div className="d-flex align-items-center gap-2">
            {!user?.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="matka-user-logged-badge">
                {user?.user?.name || user?.user?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="container-fluid custom-padding matka-betting-body pt-3 pb-4">
        {/* Back Link */}
        <button
          type="button"
          className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
          onClick={onBackToGameTypes}
        >
          <i className="bi bi-chevron-left me-1"></i>
          Back to{" "}
          {selectedMarket?.name
            ? selectedMarket.name.charAt(0) +
              selectedMarket.name.slice(1).toLowerCase()
            : "Mahadevi"}
        </button>

        {/* Title */}
        <h1 className="matka-jodi-title mb-4">JODI DIGIT BULK</h1>
        <div className="matka-game-type-selector-wrapper mb-4">
          <label className="matka-game-type-label">GAME TYPE</label>
          <MatkaSessionSelect
            betDirection={selectedGameType?.betDirection}
            value={gameType}
            onChange={handleGameTypeChange}
          />
        </div>

        {/* Enter Points & Enter Jodi Inputs Form */}
        <form onSubmit={handleAddBid} className="matka-jodi-form mb-4">
          {/* Enter Points */}
          <div className="matka-jodi-input-group mb-3">
            <label className="matka-jodi-label">ENTER POINTS</label>
            <input
              type="text"
              inputMode="numeric"
              className="matka-jodi-input"
              placeholder="Enter Points (e.g. 100)"
              value={inputPoints}
              onChange={(e) => setInputPoints(e.target.value)}
            />
          </div>

          {/* Enter Jodi */}
          <div className="matka-jodi-input-group mb-3">
            <label className="matka-jodi-label">ENTER JODI</label>
            <input
              type="text"
              className="matka-jodi-input"
              placeholder="Enter Jodi"
              value={inputJodi}
              onChange={(e) => setInputJodi(e.target.value)}
            />
          </div>

          {/* Add Bid Button */}
          <div className="d-flex justify-content-end mt-2">
            <button type="submit" className="matka-add-bid-btn">
              ADD BID
            </button>
          </div>
        </form>

        {/* Divider */}
        <div className="matka-jodi-divider mb-4" />

        {/* Bids Table */}
        <div className="matka-jodi-table-container mb-4">
          <table className="matka-jodi-table">
            <thead>
              <tr>
                <th className="th-jodi">DIGIT</th>
                <th className="th-points text-center">POINTS</th>
                <th className="th-gametype text-end">GAME TYPE</th>
              </tr>
            </thead>
            <tbody>
              {bids.map((bid) => (
                <tr key={bid.id}>
                  <td className="td-jodi">{bid.digit}</td>
                  <td className="td-points text-center">{bid.points}</td>
                  <td className="td-gametype text-end">{bid.gameType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* In-page Action Bar with Bids, Points, and CONTINUE */}
        <div className="matka-bulk-summary-bar">
          <div className="d-flex align-items-center gap-3">
            {/* Bids Counter */}
            <div className="matka-bulk-counter-pill">
              <span className="matka-bulk-counter-val">{totalBids}</span>
              <span className="matka-bulk-counter-label">Bids</span>
            </div>

            {/* Total Points Counter */}
            <div className="matka-bulk-counter-pill">
              <span className="matka-bulk-counter-val">
                {totalPoints.toLocaleString()}
              </span>
              <span className="matka-bulk-counter-label">Total Points</span>
            </div>
          </div>

          {/* Continue Action Button */}
          <button
            type="button"
            className="matka-bulk-continue-btn"
            onClick={handleContinue}
            disabled={totalBids === 0}
          >
            CONTINUE
          </button>
        </div>
      </main>

      {betModals}

      {/* Matka Footer */}
      <MatkaFooter
        onSelectTab={onSelectTab}
        onBackToMarkets={onBackToMarkets}
      />
    </div>
  );
}
